import { useCallback, useEffect, useRef, useState } from 'react'
import { ReadyState } from 'react-use-websocket'

import { useFeatureFlags } from '@context/FeatureFlagsContext'

import type { SendWebSocketJsonMessage } from '../core/types'
import type { WebSocketTransport } from '../hooks/useWebSocketTransport'

const REMOTE_SUBSCRIPTION_TIMEOUT_MS = 8000

type Options = Pick<
  WebSocketTransport,
  'coordinator' | 'enabled' | 'sendLeaderJsonMessage' | 'socketReadyState'
>

type SubscriptionsByInstance = Record<string, readonly string[]>

const getUniqueSymbols = (subscriptions: Iterable<readonly string[]>): string[] =>
  [...new Set([...subscriptions].flat())].sort()

const sendRequest = (
  sendJsonMessage: SendWebSocketJsonMessage,
  type: 'subscribe_ticks_market_data_request' | 'unsubscribe_ticks_market_data_request',
  symbols: string[]
) => {
  if (symbols.length) sendJsonMessage({ type, Data: { symbols } }, false)
}

const areSymbolsEqual = (left: readonly string[] | undefined, right: readonly string[]) =>
  left?.length === right.length && left.every((symbol, index) => symbol === right[index])

export const useTicksMarketDataSubscriptions = ({
  coordinator,
  enabled: transportEnabled,
  sendLeaderJsonMessage,
  socketReadyState,
}: Options) => {
  const { marketDataWSType } = useFeatureFlags()
  const { broadcastTickSubscriptions, isLeaderTab, subscribe } = coordinator
  const isEnabled = transportEnabled && marketDataWSType === 'notifications-hub'
  const registrationsRef = useRef(new Set<readonly string[]>())
  const subscribedSymbolsRef = useRef<string[]>([])
  const lastSeenByInstanceRef = useRef<Record<string, number>>({})
  const [localSymbols, setLocalSymbols] = useState<string[]>([])
  const [subscriptionsByInstance, setSubscriptionsByInstance] = useState<SubscriptionsByInstance>(
    {}
  )

  const updateLocalSymbols = useCallback(() => {
    setLocalSymbols(getUniqueSymbols(registrationsRef.current))
  }, [])

  const register = useCallback(
    (symbols: readonly string[]) => {
      const registration = [...symbols]
      registrationsRef.current.add(registration)
      updateLocalSymbols()

      return () => {
        registrationsRef.current.delete(registration)
        updateLocalSymbols()
      }
    },
    [updateLocalSymbols]
  )

  useEffect(() => {
    if (!isEnabled) return
    broadcastTickSubscriptions(localSymbols)
  }, [broadcastTickSubscriptions, isEnabled, localSymbols])

  useEffect(() => {
    if (!isEnabled) {
      lastSeenByInstanceRef.current = {}
      setSubscriptionsByInstance({})
      return
    }

    return subscribe((message) => {
      if (message.type === 'leader-heartbeat' && !isLeaderTab) {
        broadcastTickSubscriptions(localSymbols)
        return
      }

      if (message.type !== 'tick-subscriptions') return

      const { symbols } = message.payload
      const instanceId = message.sourceInstanceId

      if (symbols.length) {
        lastSeenByInstanceRef.current[instanceId] = Date.now()
      } else {
        delete lastSeenByInstanceRef.current[instanceId]
      }

      setSubscriptionsByInstance((current) => {
        if (areSymbolsEqual(current[instanceId], symbols)) return current

        if (!symbols.length) {
          if (!current[instanceId]) return current
          const next = { ...current }
          delete next[instanceId]
          return next
        }

        return { ...current, [instanceId]: symbols }
      })
    })
  }, [broadcastTickSubscriptions, isEnabled, isLeaderTab, localSymbols, subscribe])

  useEffect(() => {
    if (!isEnabled || !isLeaderTab) return

    const removeStaleSubscriptions = () => {
      const staleBefore = Date.now() - REMOTE_SUBSCRIPTION_TIMEOUT_MS
      const staleInstanceIds = Object.entries(lastSeenByInstanceRef.current)
        .filter(([, lastSeenAt]) => lastSeenAt < staleBefore)
        .map(([instanceId]) => instanceId)

      if (!staleInstanceIds.length) return

      staleInstanceIds.forEach((instanceId) => {
        delete lastSeenByInstanceRef.current[instanceId]
      })
      setSubscriptionsByInstance((current) => {
        const next = { ...current }
        staleInstanceIds.forEach((instanceId) => delete next[instanceId])
        return next
      })
    }

    removeStaleSubscriptions()
    const intervalId = window.setInterval(
      removeStaleSubscriptions,
      REMOTE_SUBSCRIPTION_TIMEOUT_MS / 2
    )

    return () => window.clearInterval(intervalId)
  }, [isEnabled, isLeaderTab])

  useEffect(() => {
    if (!isLeaderTab || socketReadyState !== ReadyState.OPEN) {
      subscribedSymbolsRef.current = []
      return
    }

    if (!isEnabled) {
      sendRequest(
        sendLeaderJsonMessage,
        'unsubscribe_ticks_market_data_request',
        subscribedSymbolsRef.current
      )
      subscribedSymbolsRef.current = []
      return
    }

    const nextSymbols = getUniqueSymbols([localSymbols, ...Object.values(subscriptionsByInstance)])
    const toUnsubscribe = subscribedSymbolsRef.current.filter(
      (symbol) => !nextSymbols.includes(symbol)
    )
    const toSubscribe = nextSymbols.filter(
      (symbol) => !subscribedSymbolsRef.current.includes(symbol)
    )

    sendRequest(sendLeaderJsonMessage, 'unsubscribe_ticks_market_data_request', toUnsubscribe)
    sendRequest(sendLeaderJsonMessage, 'subscribe_ticks_market_data_request', toSubscribe)
    subscribedSymbolsRef.current = nextSymbols
  }, [
    isLeaderTab,
    isEnabled,
    localSymbols,
    sendLeaderJsonMessage,
    socketReadyState,
    subscriptionsByInstance,
  ])

  return register
}
